/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * A render-time safety net.
 *
 * Without one of these, a single thrown error anywhere in the tree unmounts
 * the ENTIRE app and the user is left staring at a blank white page with no
 * indication that anything went wrong. That is exactly what a Rules-of-Hooks
 * violation in one route used to do to this app.
 *
 * Scoped per route (see App.tsx) so a broken screen stays a broken screen —
 * the sidebar, the nav, and every other route keep working.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Changing this resets the boundary — used to clear the error on navigation. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept as console output on purpose: this is the only trace a user can
    // hand back when reporting "the page went blank".
    console.error("Unhandled render error:", error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card border-danger/30 bg-danger/5 p-6" role="alert">
        <h2 className="font-serif text-lg font-semibold text-danger">
          This screen hit an error
        </h2>
        <p className="mt-1 text-sm text-ink-2">
          The rest of the app still works — use the navigation to move on, or
          reload to try this screen again.
        </p>
        <pre className="mt-3 overflow-auto rounded-lg bg-paper-3 p-3 text-xs text-ink-2">
          {error.message}
        </pre>
        <div className="mt-3 flex gap-2">
          <button className="btn-primary" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button className="btn-ghost" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
